import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Building2, Mail, MapPin, Clock, Phone, FileText, Shield, HelpCircle, RotateCcw, Scale, Users, Target, Handshake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared page wrapper
function PageWrapper({ children, onBack }) {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-primary text-white py-4 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Înapoi
          </Button>
          <Scale className="w-6 h-6 text-secondary" />
          <span className="font-serif font-bold">Judecătorul Digital</span>
        </div>
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto px-4 py-8"
      >
        {children}
      </motion.div>
      
      {/* Footer */}
      <footer className="bg-primary/95 text-white/70 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm">
          <p>© 2024 ALIANȚA PENTRU NATURA ONG ROMANIA</p>
          <p className="mt-1">Judecătorul Digital - Justiție modernă prin inteligență artificială</p>
        </div>
      </footer>
    </div>
  );
}

// Contact Page
export function ContactPage({ onBack }) {
  return (
    <PageWrapper onBack={onBack}>
      <div className="text-center mb-8">
        <Building2 className="w-12 h-12 mx-auto mb-4 text-secondary" />
        <h1 className="font-serif text-3xl font-bold text-primary">Contact și Locație</h1>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-card p-6 rounded-lg border-2 border-primary/10">
          <h2 className="font-semibold text-lg text-primary mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-secondary" />
            Compania
          </h2>
          <p className="font-bold text-lg">ALIANȚA PENTRU NATURA ONG</p>
          <p className="text-muted-foreground mt-2">Organizație neguvernamentală, non-profit</p>
        </div>
        
        <div className="bg-card p-6 rounded-lg border-2 border-primary/10">
          <h2 className="font-semibold text-lg text-primary mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-secondary" />
            Reprezentant Legal
          </h2>
          <p>Echipa Legală a ALIANȚEI PENTRU NATURA ONG</p>
        </div>
        
        <div className="bg-card p-6 rounded-lg border-2 border-primary/10">
          <h2 className="font-semibold text-lg text-primary mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-secondary" />
            Adresă
          </h2>
          <p>Calea Smârdan nr. 2</p>
          <p>Parc Industrial ATLAS, Etajul 2</p>
          <p>Galați, Județul Galați</p>
          <p className="font-semibold mt-1">România</p>
        </div>
        
        <div className="bg-card p-6 rounded-lg border-2 border-primary/10">
          <h2 className="font-semibold text-lg text-primary mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-secondary" />
            E-mail
          </h2>
          <a href="mailto:ncsllcia@gmail.com" className="text-primary hover:underline font-mono">
            ncsllcia@gmail.com
          </a>
        </div>
      </div>
      
      {/* Customer Service */}
      <div className="mt-8 bg-secondary/10 p-6 rounded-lg border-2 border-secondary/30">
        <h2 className="font-serif text-2xl font-bold text-primary mb-4 flex items-center gap-2">
          <Clock className="w-6 h-6 text-secondary" />
          Serviciu Clienți
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="font-semibold">Program:</p>
            <p className="text-lg">De luni până duminică: <strong>24/7</strong></p>
          </div>
          <div>
            <p className="font-semibold">Contact:</p>
            <p>ncsllcia@gmail.com — botchat 24/7</p>
          </div>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          Angajamentul nostru este să răspundem la toate solicitările dumneavoastră în termen de maximum 24 de ore lucrătoare, pentru a vă garanta cea mai bună experiență.
        </p>
      </div>
    </PageWrapper>
  );
}

// About Page
export function AboutPage({ onBack }) {
  const [content, setContent] = useState('');
  
  useEffect(() => {
    axios.get(`${API}/legal/about`).then(res => setContent(res.data.content)).catch(() => {});
  }, []);
  
  return (
    <PageWrapper onBack={onBack}>
      <div className="text-center mb-8">
        <Scale className="w-12 h-12 mx-auto mb-4 text-secondary" />
        <h1 className="font-serif text-3xl font-bold text-primary">Despre Noi</h1>
      </div>
      
      <div className="prose prose-lg max-w-none">
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>
    </PageWrapper>
  );
}

// Terms Page
export function TermsPage({ onBack }) {
  const [content, setContent] = useState('');
  
  useEffect(() => {
    axios.get(`${API}/legal/terms`).then(res => setContent(res.data.content)).catch(() => {});
  }, []);
  
  return (
    <PageWrapper onBack={onBack}>
      <div className="text-center mb-8">
        <FileText className="w-12 h-12 mx-auto mb-4 text-secondary" />
        <h1 className="font-serif text-3xl font-bold text-primary">Termeni și Condiții</h1>
      </div>
      
      <div className="prose prose-lg max-w-none bg-card p-8 rounded-lg border">
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>
    </PageWrapper>
  );
}

// Privacy Policy Page
export function PrivacyPage({ onBack }) {
  const [content, setContent] = useState('');
  
  useEffect(() => {
    axios.get(`${API}/legal/privacy`).then(res => setContent(res.data.content)).catch(() => {});
  }, []);
  
  return (
    <PageWrapper onBack={onBack}>
      <div className="text-center mb-8">
        <Shield className="w-12 h-12 mx-auto mb-4 text-secondary" />
        <h1 className="font-serif text-3xl font-bold text-primary">Politica de Confidențialitate</h1>
      </div>
      
      <div className="prose prose-lg max-w-none bg-card p-8 rounded-lg border">
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>
    </PageWrapper>
  );
}

// FAQ Page
export function FAQPage({ onBack }) {
  const [content, setContent] = useState('');
  
  useEffect(() => {
    axios.get(`${API}/legal/faq`).then(res => setContent(res.data.content)).catch(() => {});
  }, []);
  
  return (
    <PageWrapper onBack={onBack}>
      <div className="text-center mb-8">
        <HelpCircle className="w-12 h-12 mx-auto mb-4 text-secondary" />
        <h1 className="font-serif text-3xl font-bold text-primary">Întrebări Frecvente</h1>
      </div>
      
      <div className="prose prose-lg max-w-none bg-card p-8 rounded-lg border">
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>
    </PageWrapper>
  );
}

// Refund Policy Page
export function RefundPage({ onBack }) {
  const [content, setContent] = useState('');
  
  useEffect(() => {
    axios.get(`${API}/legal/refund`).then(res => setContent(res.data.content)).catch(() => {});
  }, []);
  
  return (
    <PageWrapper onBack={onBack}>
      <div className="text-center mb-8">
        <RotateCcw className="w-12 h-12 mx-auto mb-4 text-secondary" />
        <h1 className="font-serif text-3xl font-bold text-primary">Politica de Returnare</h1>
      </div>
      
      <div className="prose prose-lg max-w-none bg-card p-8 rounded-lg border">
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>
    </PageWrapper>
  );
}
